/**
 * MultiLocationChart — overlay all of a pour's monitoring locations on a
 * single Highcharts so operators can compare them side-by-side.
 *
 * Mirrors legacy ConXEdge `LoggerTrackChart1.aspx` (one series per
 * location) and the dashboard tabs labelled Strength / Temperature /
 * Weather. Each location's chart data is fetched ONCE (with the expensive
 * temperature-differential computation skipped server-side) and every
 * requested metric renders its own panel from that shared response — so
 * showing Strength + TTF together costs no extra requests.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  CircularProgress,
  Alert,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
} from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TableRowsIcon from '@mui/icons-material/TableRows';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import {
  poursAPI,
  type LocationStatus,
  type CalibratedRange,
  type MaturityDescriptor,
} from '../../services/pours';
import { getBaseTimeSeriesOptions } from './chartOptions';
import {
  TREND_ESTIMATE_TOOLTIP,
  declutterTargetLabels,
  extrapolatedZones,
  maturityAxisSoftMax,
  outsideTestedSpan,
  strengthAxisSoftMax,
  testedRangeBand,
} from './strengthChart';
import {
  NO_DATA_TEXT,
  SERIES_COLORS,
  buildOverlayPanels,
  markerSeries,
  metricConfig,
  strengthPointFormatter,
  targetLinePriorities,
  targetPlotLines,
  type LocationChart,
  type MetricSeries,
  type MultiChartMetric,
  type OverlayTargetLine,
} from './overlayModel';
import { locationHasMaturity } from '../../utils/locationMaturity';
import {
  maturityMeta,
  resolveStrengthUnit,
} from '../../utils/maturityDisplay';
import { apiErrorText } from '../../utils/apiError';
import { timeZoneLabel } from '../../utils/datetime';

// The overlay model (series per location, target lines, markers) lives in
// overlayModel so the phone charts can reuse it; these stay importable from
// here for existing callers.
export {
  metricConfig,
  overlayTargetLines,
  strengthEmptyText,
} from './overlayModel';
export type { MultiChartMetric, OverlayTargetLine } from './overlayModel';

interface MultiLocationChartProps {
  pourId: string;
  locations: LocationStatus[];
  /** Metrics to render, one stacked panel each, all fed from a single fetch
   *  of the per-location chart data. */
  metrics: MultiChartMetric[];
  /** The pour's stored maturity method. Only "Nurse Saul" is TTF (°C·hrs);
   *  every other value is EA (hrs). Used when no descriptor is available. */
  maturityMethod?: string;
  /** The pour's maturity naming (PourSummary.maturity); the chart
   *  responses' own descriptor is used when this is absent. */
  maturity?: MaturityDescriptor | null;
  /** The company strength unit; the chart responses' unit is used when
   *  this is absent. */
  strengthUnit?: string;
  /** IANA TZ for Highcharts time-axis labels — keep dashboard tabs in sync
   *  with the project's local timezone. */
  timezoneIana?: string;
  /** Bumped by the page on each refresh (its 5-minute poll, the refresh
   *  button): the readings reload quietly, keeping the charts mounted with
   *  their zoom. */
  refreshSignal?: number;
}

interface MetricPanelProps {
  metric: MultiChartMetric;
  seriesData: MetricSeries[];
  maturity: MaturityDescriptor;
  strengthUnit: string;
  timezoneIana?: string;
  /** Message shown when there is nothing to plot (the metric's default
   *  message when absent). */
  emptyText?: string;
  /** Target lines for the strength / maturity panels. */
  targetLines?: OverlayTargetLine[];
  /** Strength panel: the calibration's tested range, shaded on the axis. */
  strengthRange?: CalibratedRange | null;
  /** Shown under the chart (chart view only). */
  caption?: string;
}

const NO_TARGET_LINES: OverlayTargetLine[] = [];

/** One metric's panel: chart/table toggle, the overlay chart, and the flat
 *  reading table with logger filter + pagination. */
const MetricPanel: React.FC<MetricPanelProps> = ({
  metric,
  seriesData,
  maturity,
  strengthUnit,
  timezoneIana,
  emptyText,
  targetLines = NO_TARGET_LINES,
  strengthRange = null,
  caption,
}) => {
  // Chart is the default view; Table mirrors the legacy C# app's flat reading
  // grid for operators who want exact timestamped values rather than a curve.
  const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  // Logger/location filter for the table. Empty = show all loggers.
  const [loggerFilter, setLoggerFilter] = useState<string[]>([]);

  // Flatten the per-location series into one row per (location, reading),
  // sorted by time then location — the shape the table renders. Elapsed time
  // is measured from the earliest reading across all locations.
  // Any humidity series present? (Only happens on the temperature panel —
  // a T&H pour's humidity channels share it, on a secondary %RH axis.)
  const hasHumidity = seriesData.some((s) => s.isHumidity);

  const tableRows = useMemo(() => {
    const t0 = seriesData.reduce((min, s) => {
      const first = s.data[0]?.[0];
      return first != null && first < min ? first : min;
    }, Infinity);
    const rows = seriesData.flatMap((s) =>
      s.data.map(([t, v]) => ({
        logger: s.name,
        t,
        v,
        hum: !!s.isHumidity,
        // A strength outside the location's tested span is a trend estimate.
        estimate: outsideTestedSpan(t, s.span),
        elapsedMin: Math.round((t - t0) / 60000),
      })),
    );
    rows.sort((a, b) => a.t - b.t || a.logger.localeCompare(b.logger));
    return rows;
  }, [seriesData]);

  // Distinct loggers for the filter dropdown.
  const loggerOptions = useMemo(
    () => Array.from(new Set(tableRows.map((r) => r.logger))).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    [tableRows],
  );

  // Rows after applying the logger filter (empty filter = all loggers).
  const filteredRows = useMemo(
    () =>
      loggerFilter.length === 0
        ? tableRows
        : tableRows.filter((r) => loggerFilter.includes(r.logger)),
    [tableRows, loggerFilter],
  );

  // Timezone-aware formatter, built once per timezone change. Matches the
  // legacy "dd/MM/yyyy, HH:mm" display. Without a pour timezone the time is
  // UTC, as on the chart, and each row names its zone.
  const timeFmt = useMemo(
    () =>
      new Intl.DateTimeFormat('en-GB', {
        timeZone: timezoneIana || 'UTC',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }),
    [timezoneIana],
  );

  // Reset to the first page whenever the set of loggers or the view changes
  // so the user isn't stranded on a now-empty page. A refresh that only adds
  // readings keeps the page.
  const seriesKey = seriesData.map((s) => s.name).join('|');
  useEffect(() => {
    setPage(0);
  }, [seriesKey, viewMode, loggerFilter]);

  // Drop any selected loggers that no longer exist when the dataset changes.
  useEffect(() => {
    setLoggerFilter((prev) => prev.filter((name) => loggerOptions.includes(name)));
  }, [loggerOptions]);

  const cfg = useMemo(() => {
    const base = metricConfig(metric, maturity, strengthUnit);
    return metric === 'temperature' && hasHumidity
      ? { ...base, title: 'Temperature & Humidity vs Time' }
      : base;
  }, [metric, maturity, strengthUnit, hasHumidity]);

  // Overlaid locations stay as plain splines (no area fill: overlapping
  // gradients from many locations would be muddy). No preselected range
  // button: the chart opens on all data anyway, and a preselected button is
  // clicked again on every update, which would undo the user's zoom each
  // time the page refreshes the readings. Built once per change of the
  // readings, so other re-renders do not update the chart.
  const options = useMemo(() => {
    const colourOf = (i: number): string => SERIES_COLORS[i % SERIES_COLORS.length];
    const lines = seriesData.map((s, i) => {
      const colour = colourOf(i);
      return {
        id: s.id,
        name: s.isHumidity ? `${s.name} (RH)` : s.name,
        type: 'spline',
        data: s.data,
        marker: { enabled: false, radius: 2 },
        color: colour,
        ...(s.isHumidity
          ? {
              yAxis: 1,
              dashStyle: 'ShortDash' as Highcharts.DashStyleValue,
              tooltip: { valueSuffix: ' %' },
            }
          : {}),
        // Strength outside the location's tested span is a trend estimate:
        // dashed and faded, and its tooltip says so.
        ...(metric === 'strength' && s.span
          ? {
              zoneAxis: 'x',
              zones: extrapolatedZones(s.span, colour),
              tooltip: { pointFormatter: strengthPointFormatter(s.span) },
            }
          : {}),
      };
    }) as Highcharts.SeriesSplineOptions[];
    const markers = seriesData.flatMap((s, i) =>
      s.id && s.markers && s.markers.length > 0 ? [markerSeries(s, colourOf(i))] : [],
    );
    const targetValues = targetLines.map((line) => line.value);
    const dataMax = seriesData.reduce(
      (max, s) => s.data.reduce((m, [, v]) => (v > m ? v : m), max),
      -Infinity,
    );
    const priorityById = targetLinePriorities(targetLines);

    return Highcharts.merge<Highcharts.Options>(
      getBaseTimeSeriesOptions(cfg.title, {
        timezoneIana,
        height: 550,
        rangeButtons: [
          { type: 'hour', count: 6, text: '6h' },
          { type: 'day', count: 1, text: '1d' },
          { type: 'day', count: 3, text: '3d' },
          { type: 'week', count: 1, text: '1w' },
          { type: 'all', text: 'All' },
        ],
      }),
      {
        // Target labels closer than about 16 px are hidden after every
        // redraw and zoom, keeping the lines not every location has reached.
        ...(targetLines.length > 0
          ? {
              chart: {
                events: {
                  render(this: Highcharts.Chart) {
                    declutterTargetLabels(this, priorityById);
                  },
                },
              },
            }
          : {}),
        legend: {
          enabled: true,
          align: 'right',
          verticalAlign: 'top',
          layout: 'vertical',
        },
        // With humidity series present, temperature keeps the left axis and
        // humidity gets a fixed 0-100% axis on the right.
        yAxis: hasHumidity
          ? [
              { title: { text: cfg.yAxisTitle }, opposite: false },
              {
                title: { text: 'Relative Humidity (%)' },
                min: 0,
                max: 100,
                opposite: true,
              },
            ]
          : {
              title: { text: cfg.yAxisTitle },
              opposite: false,
              plotLines: targetPlotLines(targetLines),
              // Strength starts at 0, leaves room for target lines above the
              // curve and shades the tested range behind it.
              ...(metric === 'strength'
                ? {
                    min: 0,
                    softMax: strengthAxisSoftMax(strengthRange, targetValues, dataMax),
                    plotBands: testedRangeBand(strengthRange),
                  }
                : {}),
              // Maturity leaves room for the target lines not reached yet.
              ...(metric === 'maturity' ? { softMax: maturityAxisSoftMax(targetValues) } : {}),
            },
        tooltip: {
          // Per-curve hover: show just the logger whose line is under the
          // cursor. A shared tooltip anchored to the nearest x-slice and, when
          // loggers logged at slightly different times, stayed glued to whichever
          // 1-2 curves were closest, so overlapping/crossing curves (e.g. a higher
          // logger) could never be reached or highlighted individually.
          shared: false,
          split: false,
          valueDecimals: cfg.valueDecimals,
          valueSuffix: cfg.valueSuffix,
        },
        plotOptions: {
          series: {
            // Only track when the cursor is actually on a line, so the user can
            // move between overlapping curves instead of staying snapped to the
            // nearest one. Hovering a curve dims the others and shows its marker
            // so the active logger stands out.
            stickyTracking: false,
            states: {
              hover: { lineWidthPlus: 1 },
              inactive: { opacity: 0.3 },
            },
            marker: {
              enabled: false,
              states: { hover: { enabled: true, radius: 4 } },
            },
          },
        },
        series: [...lines, ...markers],
      },
    );
  }, [cfg, hasHumidity, metric, seriesData, strengthRange, targetLines, timezoneIana]);

  if (seriesData.length === 0) {
    return (
      <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          No data available
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {emptyText ?? (metric === 'weather' ? 'No weather readings for this pour.' : NO_DATA_TEXT)}
        </Typography>
      </Paper>
    );
  }


  return (
    <Paper elevation={2} sx={{ p: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {cfg.title}
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={viewMode}
          onChange={(_e, next) => {
            if (next) setViewMode(next);
          }}
          aria-label="View mode"
        >
          <ToggleButton value="chart" aria-label="Chart view">
            <ShowChartIcon fontSize="small" sx={{ mr: 0.5 }} /> Chart
          </ToggleButton>
          <ToggleButton value="table" aria-label="Table view">
            <TableRowsIcon fontSize="small" sx={{ mr: 0.5 }} /> Table
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {viewMode === 'chart' ? (
        <>
          <HighchartsReact
            highcharts={Highcharts}
            constructorType="stockChart"
            options={options}
          />
          {caption && (
            <Typography
              variant="caption"
              component="p"
              color="text.secondary"
              sx={{ mt: 1 }}
              data-testid="strength-range-caption"
            >
              {caption}
            </Typography>
          )}
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel id={`logger-filter-label-${metric}`} shrink>
                Logger / Location
              </InputLabel>
              <Select
                labelId={`logger-filter-label-${metric}`}
                multiple
                value={loggerFilter}
                onChange={(e) =>
                  setLoggerFilter(
                    typeof e.target.value === 'string'
                      ? e.target.value.split(',')
                      : e.target.value,
                  )
                }
                input={<OutlinedInput notched label="Logger / Location" />}
                displayEmpty
                renderValue={(selected) =>
                  selected.length === 0 ? 'All loggers' : selected.join(', ')
                }
              >
                {loggerOptions.map((name) => (
                  <MenuItem key={name} value={name}>
                    <Checkbox checked={loggerFilter.includes(name)} />
                    <ListItemText primary={name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {loggerFilter.length > 0 && (
              <Button size="small" onClick={() => setLoggerFilter([])}>
                Clear
              </Button>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
              {filteredRows.length} reading{filteredRows.length === 1 ? '' : 's'}
            </Typography>
          </Box>
          <TableContainer sx={{ maxHeight: 550 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Logger / Location</TableCell>
                  <TableCell>Log Time</TableCell>
                  <TableCell align="right">Elapsed (h:mm)</TableCell>
                  <TableCell align="right">
                    {hasHumidity ? 'Value (°C / %RH)' : cfg.yAxisTitle}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((r, i) => (
                    <TableRow key={`${r.logger}-${r.t}-${i}`} hover>
                      <TableCell>{r.logger}</TableCell>
                      <TableCell>{`${timeFmt.format(new Date(r.t))} ${timeZoneLabel(r.t, timezoneIana)}`}</TableCell>
                      <TableCell align="right">
                        {`${Math.floor(r.elapsedMin / 60)}:${String(
                          r.elapsedMin % 60,
                        ).padStart(2, '0')}`}
                      </TableCell>
                      <TableCell align="right">
                        {r.v.toFixed(cfg.valueDecimals)}
                        {hasHumidity ? (r.hum ? ' %' : ' °C') : ''}
                        {r.estimate && <span title={TREND_ESTIMATE_TOOLTIP}> (estimate)</span>}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            onPageChange={(_e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </>
      )}
    </Paper>
  );
};

export const MultiLocationChart: React.FC<MultiLocationChartProps> = ({
  pourId,
  locations,
  metrics,
  maturityMethod,
  maturity,
  strengthUnit,
  timezoneIana,
  refreshSignal,
}) => {
  const [charts, setCharts] = useState<LocationChart[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Loads are keyed on the location IDs and metrics, never on the array:
  // the page hands over a new `locations` array on every poll, which must
  // not reload (and unmount) the charts. The latest array is read through a
  // ref when a load runs.
  const locationIds = locations.map((l) => l.id).join(',');
  const metricsKey = metrics.join(',');
  const locationsRef = useRef(locations);
  locationsRef.current = locations;

  // Each load is numbered so an older, slower response never replaces a
  // newer one. A quiet load (the page's refresh) keeps the charts on screen
  // and keeps a location's last data when its reload fails.
  const loadSeq = useRef(0);
  const load = useCallback(async (quiet: boolean) => {
    const seq = ++loadSeq.current;
    if (!quiet) {
      setLoading(true);
      setError(null);
    }
    // Strength/maturity panels only read the ea series — ask the backend to
    // skip the temperature/evaporation work too (incl. the slow Cosmos read
    // on completed pours), and only for the locations that accrue maturity.
    const eaOnly = metricsKey.split(',').every((m) => m === 'strength' || m === 'maturity');
    const wanted = eaOnly
      ? locationsRef.current.filter(locationHasMaturity)
      : locationsRef.current;
    try {
      const results = await Promise.all(
        wanted.map(async (loc): Promise<LocationChart | null> => {
          try {
            // includeDiff=false: the overlay charts never plot differentials,
            // and skipping them server-side keeps each request cheap.
            const chart = await poursAPI.getLocationChartData(
              pourId, loc.id, undefined, undefined, false, eaOnly ? 'ea' : 'all',
            );
            return {
              id: loc.id,
              // Location name first — "Castle Hill Humidity" reads far
              // better in the legend than "AU001930-4".
              name: loc.name || loc.logger_code || loc.id,
              isHumidity: (loc.channel_type || '').toUpperCase() === 'HUMIDITY',
              hasMaturity: locationHasMaturity(loc),
              chart,
            };
          } catch (e) {
            // Fail one location at a time — keep the chart usable
            return null;
          }
        }),
      );
      if (seq !== loadSeq.current) return;
      setCharts((previous) =>
        wanted.flatMap((loc, i) => {
          const fresh = results[i];
          if (fresh) return [fresh];
          const kept = quiet ? previous.find((p) => p.id === loc.id) : undefined;
          return kept ? [kept] : [];
        }),
      );
    } catch (e: any) {
      if (seq === loadSeq.current && !quiet) setError(apiErrorText(e, 'Failed to load chart data'));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [pourId, metricsKey]);

  // A different pour, set of locations or metrics loads from scratch.
  useEffect(() => {
    load(false);
  }, [load, locationIds]);

  // The page's refresh reloads quietly. Only a change of the signal
  // triggers it (not the first render).
  const lastRefreshSignal = useRef(refreshSignal);
  useEffect(() => {
    if (refreshSignal === lastRefreshSignal.current) return;
    lastRefreshSignal.current = refreshSignal;
    load(true);
  }, [refreshSignal, load]);

  // Pour-level naming from the props, else from the chart responses (every
  // location of a pour shares them), else the backend rule on the method.
  const responseMaturity = charts.find((c) => c.chart.maturity)?.chart.maturity;
  const meta = useMemo(
    () => maturityMeta(maturity ?? responseMaturity ?? maturityMethod),
    [maturity, responseMaturity, maturityMethod],
  );
  const unit = resolveStrengthUnit(
    strengthUnit,
    charts.find((c) => c.chart.strength_unit)?.chart.strength_unit,
  );

  // Slice each requested metric out of the shared per-location responses,
  // with what the strength and maturity panels draw over the readings: each
  // location's tested span and achievement markers, the target lines, and
  // the strength panel's tested range and caption.
  const panels = useMemo(
    () => buildOverlayPanels(charts, metricsKey, meta, unit),
    [charts, metricsKey, meta, unit],
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {panels.map((panel) => (
        <MetricPanel
          key={panel.metric}
          metric={panel.metric}
          seriesData={panel.seriesData}
          maturity={meta}
          strengthUnit={unit}
          timezoneIana={timezoneIana}
          emptyText={panel.emptyText}
          targetLines={panel.targetLines}
          strengthRange={panel.strengthRange}
          caption={panel.caption}
        />
      ))}
    </Box>
  );
};

export default MultiLocationChart;
